import { useState, useEffect, useMemo } from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { findLast, get } from 'lodash';

import api from 'api';
import {
  BlogResponse,
  ListResponse,
  MMScriptStatus,
  RequestDetails,
  ScriptStatus,
  SelectOption,
} from 'types';

import useCurrentUser from 'hooks/useCurrentUser';
import toast from 'react-hot-toast';
import useSendCustomerNotification from 'hooks/useSendCustomerNotification';
import { Landlord } from 'types/property';
import CheckIcon from 'pages/Requests/CheckIcon';
import { useForm } from 'react-hook-form';
import { ManualCharge } from 'pages/RequestDetails/useRequestDetails';
import useCompanyConfig from 'hooks/useCompanyConfig';

type CopayItem = {
  id: number;
  amount_due: number;
};

async function getRequestById({
  queryKey,
}: QueryFunctionContext): Promise<RequestDetails | undefined> {
  const [, id, order, status] = queryKey;
  if (!id) {
    return;
  }
  const { data } = await api.get<RequestDetails>(
    `/requests/${id}/?is_sequential_order_number=true`,
    {
      params: { order, status },
    }
  );
  return data;
}

async function getUserById({ queryKey }: QueryFunctionContext) {
  const [, userId] = queryKey;
  if (!userId) return;
  const { data } = await api.get<Landlord>(`/users/${userId}/`);
  return data;
}

async function getMessageTemplates() {
  const { data } = await api.get<ListResponse<BlogResponse>>(
    `/support/templates/`,
    {
      params: { content_type: 'template' },
    }
  );
  return data?.results;
}

export function useOrderDetails(orderId: string) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState<'service' | 'product' | 'none'>(
    'none'
  );
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>();
  const [sendChannels, setSendChannels] = useState({
    sms: true,
    email: false,
    push: false,
  });
  const [paymentUrl, setPaymentUrl] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [copayItems, setCopayItems] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isCharging, setIsCharging] = useState(false);
  const [manualChargeType, setManualChargeType] = useState<
    'payable' | 'receivable'
  >('receivable');
  const form = useForm<ManualCharge>();

  const { sendNotification } = useSendCustomerNotification();
  const queryCach = queryClient.getQueryCache().getAll();
  const { isPropertyManager } = useCompanyConfig();

  const [, , order, status] =
    (findLast(queryCach, (q) => q.queryKey[0] === 'requests')
      ?.queryKey as string[]) || [];

  const { data: messageTemplates } = useQuery(
    ['message_templates'],
    getMessageTemplates,
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data, isLoading, refetch } = useQuery(
    ['request_details', orderId, order, status],
    getRequestById
  );
  const { data: landlord } = useQuery(
    ['landlord', data?.company_property?.landlord],
    getUserById,
    { enabled: currentUser?.company?.type === 'property_manager' }
  );
  const completeRequest = useMutation(
    'complete_request',
    async () => {
      await api.post(`/requests/${data?.id}/complete/`);
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['request_details']);
      },
    }
  );

  const updateScriptStatus = useMutation(
    'update_script_status',
    async ({
      drugId,
      status,
    }: {
      drugId: number;
      status: ScriptStatus | MMScriptStatus;
    }) => {
      const { data } = await api.post<{ status: ScriptStatus }>(
        `/requests/${orderId}/prescriptions/${drugId}/status/?is_sequential_order_number=true`,
        { status }
      );
      return { ...data, drugId };
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', orderId]);
      },
    }
  );

  const updatePreference = useMutation(
    'update_preference',
    async (datas: {
      preference_date: string | null;
      preference_time: string | null;
    }) => {
      const { data: res } = await api.patch<{
        preference_date: string;
        preference_time: string;
      }>(`/requests/${data?.id}/`, datas);
      return { ...res };
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', orderId]);
      },
    }
  );

  function onChangeTime(value: {
    preference_date: string | null;
    preference_time: string | null;
  }) {
    if (!data) return;

    updatePreference.mutate({
      ...value,
    });
  }

  async function onChangeStatus(status: ScriptStatus, drugId: number) {
    if (status) {
      await updateScriptStatus.mutateAsync({ drugId, status });
    }
  }

  function onChangePriority(newValue: string | number | undefined) {
    if (data) {
      api
        .patch(`/requests/${data.id}/`, {
          priority: newValue || null,
        })
        .then(() => {
          refetch();
        })
        .catch(() => { });
    }
  }

  function complete() {
    completeRequest.mutateAsync();
  }

  async function chargeCard(datas: any) {
    const data = await api.post<any>(`/payment/charge/`, datas);
    refetch();
    return data;
  }

  const deleteOrder = (closeModal: () => void) => async () => {
    try {
      await api.delete(`/requests/${orderId}/delete/`);
      closeModal();
    } catch (error) {
      closeModal();
      toast.error(get(error, 'response.data.error_message', 'Error'));
    }
  };

  const sendMessage = async () => {
    if (!data?.id) {
      return;
    }
    if (message.trim() === '') return;
    try {
      await api.post(`/requests/${data.id}/message-customer/`, {
        message: message,

        is_sms: sendChannels.sms,
        is_email: sendChannels.email,
        is_app_push: sendChannels.push,
        // customer_ids: [data?.customer.id],
      });
      toast.success('Message sent successfully');
      setShowMessageModal(false);
      setMessage('');
      refetch();
    } catch (error) {
      toast.error('Something went wrong');
    }
  };
  const getPaymentUrl = async () => {
    if (!data?.id) {
      return;
    }
    const res = await sendNotification({
      requestId: data?.id,
      types: {
        is_sms: false,
        is_email: false,
        is_app_push: false,
      },
      request_drugs:
        data?.prescriptions?.map(({ amount_due }) => ({ amount_due })) || [],
    });

    setPaymentUrl(res.data.payment_link);
  };

  const chargeManually = async (closeModal: () => void) => {
    try {
      if (!form.getValues('payment_method')) {
        toast.error('Payment Type is required');
        return;
      }
      if (!form.getValues('payer_type') && isPropertyManager) {
        toast.error('Payer Type is required');
        return;
      }
      setIsCharging(true);
      const body: any = {
        customer: data?.customer.id,
        copay_items: copayItems,
        payment_method: form.getValues('payment_method'),
        transaction_type: manualChargeType,
      };
      if (isPropertyManager) {
        body.payer_type = form.getValues('payer_type');
      }
      const res = await api.post<any>(`/payment/charge-manual/`, body);
      refetch();
      closeModal();
      toast.success('Successfully charged');
      return res;
    } catch (error) {
      const message = get(error, 'response.data.detail', 'Error try again');
      toast.error(message);
    } finally {
      setIsCharging(false);
    }
  };

  const orderItems = useMemo(() => {
    return data?.prescriptions?.map((item) => ({
      checkbox: (
        <div
          onClick={(val) => {
            val.stopPropagation();
            const include = copayItems.find((i) => i.id === item.id);

            if (include) {
              setCopayItems(copayItems.filter((i) => i.id !== item.id));
            } else {
              setCopayItems([
                ...copayItems,
                {
                  id: item.id,
                  amount_due:
                    manualChargeType === 'payable'
                      ? item.payable_amount_due
                      : item.amount_due,
                },
              ]);
            }
          }}
        >
          <CheckIcon
            emptySelect={!copayItems.find((i) => i.id === item.id)}
            allSelected={!!copayItems.find((i) => i.id === item.id)}
          />
        </div>
      ),
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      amount_due: item.amount_due,
      payable_amount_due: item.payable_amount_due,
      type:
        item?.offering_type?.charAt(0).toUpperCase() +
        item?.offering_type?.slice(1),
    }));
  }, [data?.prescriptions, copayItems, manualChargeType]);

  useEffect(() => {
    if (data?.prescriptions) {
      if (manualChargeType === 'receivable') {
        const receivableCopayItems: CopayItem[] =
          data?.prescriptions?.reduce((pr, curr) => {
            if (Number(curr.amount_due) > 0) {
              return [
                ...pr,
                { id: curr.id, amount_due: Number(curr.amount_due) || 0 },
              ];
            }

            return pr;
          }, [] as CopayItem[]) || [];
        setCopayItems(receivableCopayItems);
      }

      if (manualChargeType === 'payable') {
        const payableCopayItems: CopayItem[] =
          data?.prescriptions?.reduce((pr, curr) => {
            if (Number(curr.payable_amount_due) > 0) {
              return [
                ...pr,
                { id: curr.id, amount_due: Number(curr.payable_amount_due) || 0 },
              ];
            }

            return pr;
          }, [] as CopayItem[]) || [];
        setCopayItems(payableCopayItems);
      }
      // setCopayItems(items);
      setAllSelected(true);
    }
  }, [data?.prescriptions, manualChargeType]);

  return {
    refetch,
    data,
    isLoading,
    landlord,
    markDisabled: Boolean(
      data?.prescriptions.some(
        (drug) =>
          drug.status !== 'ready_for_delivery' &&
          drug.status !== 'cancel_script'
      )
    ),
    deleteOrder,
    onChangeStatus,
    complete,
    chargeCard,
    onChangeTime,
    onChangePriority,
    message,
    setMessage,
    showMessageModal,
    setShowMessageModal,
    sendChannels,
    setSendChannels,
    selectedTemplate,
    setSelectedTemplate,
    sendMessage,
    getPaymentUrl,
    paymentUrl,
    copayItems,
    setCopayItems,
    allSelected,
    setAllSelected,
    isCharging,
    setIsCharging,
    // manualPaymentType,
    // setManualPaymentType,
    orderItems,
    onChargeManually: chargeManually,
    messageTemplates: messageTemplates?.map((temp) => ({
      label: temp.title,
      value: temp?.content,
    })),
    showEditModal,
    setShowEditModal,
    editType,
    setEditType,
    form,
    manualChargeType,
    setManualChargeType,
  };
}
